import { pngPixelsOf, sniffImage } from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import { imageBoxStyles, imageEmptyStyles, imageMissingStyles } from '@pages/ReportDesigns.styles';
import { reportImageLimits } from '@store/api/reportDesignEndpoints';
import { useRef, useState } from 'react';

/**
 * What the picker has to show for the image it stands for: none chosen, one being read, one
 * on screen, one the server no longer has, or one that could not be read for another reason.
 */
export type ReportImageStatus = 'empty' | 'loading' | 'ready' | 'missing' | 'unavailable';

interface Props {
  label: string;
  /** A sentence under the control. */
  help?: string;
  status: ReportImageStatus;
  /** An address the picture can be shown from, for `ready`. */
  picture?: string;
  /**
   * What stands in the box while no image is chosen, in place of the sentence that says so:
   * for an owner that then draws another image, which it can show there.
   */
  empty?: React.ReactNode;
  /**
   * Stores a file that passed the tests below. The picker shows that it is busy until
   * the promise settles; what the stored image is called afterwards is its owner's to keep.
   */
  onPick: (file: File) => Promise<unknown>;
  onRemove: () => void;
  /**
   * Also for as long as a file handed to `onPick` is on its way, where the picker can be
   * mounted anew meanwhile. It then knows of no file and would offer Remove, which the
   * stored file undoes when it is put in. Only the owner, who keeps the upload, can tell.
   */
  disabled?: boolean;
}

/** What is wrong with a file that was picked: a key of the localization CMS. */
type Problem = 'ReportDesign_Image_TooLarge' | 'ReportDesign_Image_WrongType' | 'ReportDesign_Image_TooManyPixels';

/**
 * One uploaded image of a report: a design's logo or picture, a brand's logo. It shows the
 * image, takes a file for it and lets it be taken off; where the bytes go and come from is
 * its owner's business.
 *
 * A file is held to the server's tests of its outside before it is handed on: its size, what
 * its first bytes say it is and, for a PNG, the pixels its header states. So the usual file
 * the server would refuse is never sent; one that is damaged inside is still the server's to
 * refuse, in its own words.
 */
const ReportImagePicker: React.FC<Props> = ({ label, help, status, picture, empty, onPick, onRemove, disabled }) => {
  const { t } = useLocalization();

  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [storing, setStoring] = useState(false);

  const pick = async (file: File) => {
    setProblem(null);

    if (file.size > reportImageLimits.bytes) {
      setProblem('ReportDesign_Image_TooLarge');

      return;
    }

    const head = new Uint8Array(await file.slice(0, 24).arrayBuffer());
    const format = sniffImage(head);

    if (!format) {
      setProblem('ReportDesign_Image_WrongType');

      return;
    }

    if (format === 'png' && pngPixelsOf(head) > reportImageLimits.pngPixels) {
      setProblem('ReportDesign_Image_TooManyPixels');

      return;
    }

    setStoring(true);

    try {
      await onPick(file);
    } finally {
      setStoring(false);
    }
  };

  const body = () => {
    if (status === 'missing') return <Box sx={imageMissingStyles}>{t('ReportDesign_Image_Missing')}</Box>;
    if (status === 'unavailable') return <Box sx={imageEmptyStyles}>{t('ReportDesign_Image_Unavailable')}</Box>;
    if (status === 'ready' && picture) return <Box sx={imageBoxStyles}><img src={picture} alt={label} /></Box>;
    if (status === 'empty') return empty ?? <Box sx={imageEmptyStyles}>{t('ReportDesign_Image_None')}</Box>;

    return <Box sx={imageEmptyStyles} />;
  };

  const uploadLabel = () => {
    if (status === 'missing') return t('ReportDesign_Image_UploadAgain');

    return status === 'empty' ? t('Common_Upload') : t('ReportDesign_Image_Replace');
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>

      {body()}
      {(storing || status === 'loading') && <LinearProgress />}

      <Typography variant="caption" color={problem ? 'error' : 'text.secondary'}>
        {t(problem ?? 'ReportDesign_Image_Hint')}
      </Typography>
      {help && <Typography variant="caption" color="text.secondary">{help}</Typography>}

      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          sx={buttonStyles}
          disabled={disabled || storing}
          onClick={() => input.current?.click()}
        >
          {uploadLabel()}
        </Button>
        {status !== 'empty' && (
          <Button size="small" color="inherit" sx={buttonStyles} disabled={disabled || storing} onClick={onRemove}>
            {t('ReportDesign_Image_Remove')}
          </Button>
        )}
      </Stack>

      <input
        ref={input}
        type="file"
        hidden
        accept="image/png,image/jpeg"
        onChange={(event) => {
          const file = event.target.files?.[0];

          // Emptied so that picking the same file again is a change.
          event.target.value = '';
          if (file) void pick(file);
        }}
      />
    </Stack>
  );
};

export default ReportImagePicker;
